import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { profileContext } from "./profile.provider"
import { schoolContext } from "./school.provider"
import { subscribeToNotebook, subscribeToSharedNotebook } from "../utils/firestore.utils"

export const notebookContext = React.createContext()

const NO_ENTRIES = []

// Il quaderno del maestro (src/utils/notebook.utils.js), ascoltato finché
// c'è qualcuno: serve alla sua pagina e al contenuto delle lezioni, che ci
// prende e ci salva lezioni ed esercizi. È uno solo, qualunque circolo si
// guardi.
//
// Accanto ci sono le voci che i colleghi condividono nel circolo che si
// guarda. Quelle si cominciano ad ascoltare solo quando servono — la prima
// volta che si apre il quaderno o il programma di una lezione (`loadShared`)
// — e non all'apertura dell'app: chi il quaderno non lo apre non le paga.
// Ogni voce arriva segnata: `isMine`, e di un collega `authorName`, dai
// maestri del circolo che si ascoltano già.
const NotebookProvider = ({ children }) => {
	const { coach } = useContext(profileContext)
	const { selectedSchool, getSelectedSchool } = useContext(schoolContext) ?? {}
	const coachID = coach?.coachID

	// di chi è quello che si è letto: cambiando account non si vede, nemmeno
	// per un attimo, il quaderno di chi c'era prima
	const [notebook, setNotebook] = useState({ coachID: null, entries: NO_ENTRIES, failed: false })
	const [wantsShared, setWantsShared] = useState(false)
	const [shared, setShared] = useState({ key: null, entries: NO_ENTRIES })

	// Se Firestore il quaderno non lo dà — le regole non lo conoscono ancora,
	// o la lettura è rifiutata — non si resta ad aspettare per sempre: la
	// risposta è arrivata, ed è che non si legge.
	useEffect(() => {
		if (!coachID) return
		return subscribeToNotebook(
			coachID,
			entries => setNotebook({ coachID, entries, failed: false }),
			() => setNotebook({ coachID, entries: NO_ENTRIES, failed: true }),
		)
	}, [coachID])

	// Se quelle dei colleghi non si leggono il quaderno resta il proprio:
	// non è un motivo per non aprirlo
	const sharedKey = wantsShared && coachID && selectedSchool ? `${coachID}/${selectedSchool}` : null
	useEffect(() => {
		if (!sharedKey) return
		const [me, schoolID] = sharedKey.split("/")
		return subscribeToSharedNotebook(
			schoolID,
			me,
			entries => setShared({ key: sharedKey, entries }),
			() => setShared({ key: sharedKey, entries: NO_ENTRIES }),
		)
	}, [sharedKey])

	const loadShared = useCallback(() => setWantsShared(true), [])

	const isLoaded = Boolean(coachID) && notebook.coachID === coachID
	const coaches = getSelectedSchool?.()?.coaches
	const colleagues = coaches && !Array.isArray(coaches) ? coaches : null

	const entries = useMemo(() => {
		if (!isLoaded) return NO_ENTRIES
		const mine = notebook.entries.map(entry => ({ ...entry, isMine: true }))
		// Di chi è uscito dal circolo nel frattempo non si mostra niente,
		// anche se il server non ha ancora tolto la condivisione
		const others =
			shared.key === sharedKey
				? shared.entries
						.filter(entry => entry.coachID !== coachID)
						.filter(entry => !colleagues || colleagues[entry.coachID])
						.map(entry => ({
							...entry,
							isMine: false,
							authorName: colleagues?.[entry.coachID]?.coachName ?? "",
						}))
				: NO_ENTRIES
		return [...mine, ...others]
	}, [isLoaded, notebook.entries, shared, sharedKey, coachID, colleagues])

	return (
		<notebookContext.Provider
			value={{
				entries,
				schoolID: selectedSchool ?? null,
				schoolName: getSelectedSchool?.()?.schoolName ?? "",
				isLoading: Boolean(coachID) && !isLoaded,
				hasFailed: isLoaded && notebook.failed,
				loadShared,
				getEntry: entryID => entries.find(entry => entry.entryID === entryID) ?? null,
			}}>
			{children}
		</notebookContext.Provider>
	)
}

export default NotebookProvider
